import { useLocation, useNavigate } from 'react-router-dom';
import type {
  LoginInitialRequestDto,
  LoginMfaRequiredResponseDto,
} from '@/features/auth-login';
import { login, sendOtpCode } from '@/features/auth-login';
import { useRequestStatus, useSessionIdle } from '@/shared/lib';
import { type Session, useSessionStore } from '@/entities/session';
import { MFA_IDENTITY_TYPES, type MfaStateData } from '@/entities/mfa-identity';

export type UseLoginLocationState = {
  mfaData?: MfaStateData;
};

const useLogin = () => {
  const {
    error: loginError,
    setError: setLoginError,
    isLoading: isLoginLoading,
    setIsLoading: setIsLoginLoading,
  } = useRequestStatus();
  const { setSession } = useSessionStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkReturnPageValidity } = useSessionIdle();

  const { mfaData } = (location.state as UseLoginLocationState) ?? {};

  const runLoginProcess = async (loginArgs: LoginInitialRequestDto) => {
    try {
      setIsLoginLoading(true);
      const initLoginResponseData = await login<LoginInitialRequestDto>(
        loginArgs,
        {
          throwOnError: false,
        }
      );

      if ('accessToken' in initLoginResponseData) {
        const data = initLoginResponseData as Session;
        setSession(data);
        setLoginError(null);

        const { isValid, page } = checkReturnPageValidity();

        setTimeout(() => {
          navigate(isValid && page ? page : '/');
        }, 100);
      }

      if (
        'code' in initLoginResponseData &&
        'message' in initLoginResponseData
      ) {
        if (initLoginResponseData.code === 'MULTI_FACTOR_REQUIRED') {
          await handleMfa(
            loginArgs,
            initLoginResponseData as LoginMfaRequiredResponseDto
          );
        } else {
          setLoginError(initLoginResponseData.message);
        }
      } else {
        throw new Error('Bad response from server');
      }
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleMfa = async (
    loginArgs: LoginInitialRequestDto,
    mfaRequiredResponse: LoginMfaRequiredResponseDto
  ) => {
    const { state, identityTypes } = mfaRequiredResponse.data;
    const isOneMfaLoginMethodEnabled =
      identityTypes.filter(identity => identity.isEnabled).length === 1;

    if (isOneMfaLoginMethodEnabled) {
      if (identityTypes[0].name === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR) {
        navigate('/login/mfa-code', {
          state: {
            loginArgs,
            mfaType: identityTypes[0].name,
            mfaState: state,
            identityTypes,
          },
        });
      } else {
        try {
          let otpCodeData;

          const remaining =
            (mfaData?.[identityTypes[0].name]?.resendUntil ?? 0) - Date.now();

          const shouldSendOtp = remaining <= 0;

          if (shouldSendOtp) {
            otpCodeData = await sendOtpCode({
              state,
              identityType: identityTypes[0].name,
            });
          }

          navigate('/login/mfa-code', {
            state: {
              loginArgs,
              mfaType: identityTypes[0].name,
              mfaState: state,
              otpCodeData,
              identityTypes,
              mfaData: {
                ...mfaData,
                [identityTypes[0].name]: otpCodeData
                  ? {
                      maskedContact: otpCodeData.maskedContact,
                      resendUntil: Date.now() + otpCodeData.resendIn,
                    }
                  : mfaData?.[identityTypes[0].name],
              },
            },
          });
        } catch (error) {
          setLoginError(
            error instanceof Error ? error.message : 'Unknown error'
          );
        }
      }
    } else {
      navigate('/login/mfa-methods', {
        state: {
          loginArgs,
          identityTypes,
          mfaState: state,
          mfaData,
        },
      });
    }
  };

  return {
    runLoginProcess,
    loginError,
    isLoginLoading,
  };
};

export { useLogin };
